
import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ReviewActionButtonsProps {
  reviewId: string;
  providerId?: string;
  isPublic: boolean;
  onVisibilityToggle: (reviewId: string, isPublic: boolean) => Promise<void>;
  onDeleteReview: (reviewId: string) => void;
  isProcessing: boolean;
}

const ReviewActionButtons = ({
  reviewId,
  providerId,
  isPublic,
  onVisibilityToggle,
  onDeleteReview,
  isProcessing,
}: ReviewActionButtonsProps) => {
  const [isToggling, setIsToggling] = useState(false);

  const handleVisibilityToggle = async (checked: boolean) => {
    if (isToggling || isProcessing) return;
    
    setIsToggling(true);
    try {
      await onVisibilityToggle(reviewId, checked);
    } catch (error) {
      console.error("Error toggling visibility:", error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center">
        <Switch
          id={`visibility-${reviewId}`}
          checked={isPublic !== false}
          onCheckedChange={handleVisibilityToggle}
          disabled={isProcessing || isToggling}
        />
        <label htmlFor={`visibility-${reviewId}`} className="ml-2 text-sm">
          {isPublic !== false ? (
            <span className="flex items-center text-green-600">
              <Eye className="h-3.5 w-3.5 mr-1" />
              Public
            </span>
          ) : (
            <span className="flex items-center text-gray-500">
              <EyeOff className="h-3.5 w-3.5 mr-1" />
              Hidden
            </span>
          )}
        </label>
      </div>
      
      {providerId && (
        <Button
          variant="outline" 
          size="sm"
          asChild
          className="text-whatsapp border-whatsapp/20 hover:bg-whatsapp-light hover:text-whatsapp"
        >
          <Link to={`/service/${providerId}`} target="_blank">
            <ExternalLink className="h-4 w-4 mr-1" />
            View
          </Link>
        </Button>
      )}
      
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
            disabled={isProcessing}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => onDeleteReview(reviewId)}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewActionButtons;
